import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiFilter,
  FiImage,
  FiZoomIn,
} from "react-icons/fi";
import { galleryAPI } from "../services/api";
import { resolveImageUrl } from "../utils/helpers";
import { useDataRefresh } from "../hooks/useDataRefresh";
import LoadingSpinner from "../components/common/LoadingSpinner";

const CATEGORIES = [
  "All",
  "Campus",
  "Events",
  "Sports",
  "Academic",
  "Cultural",
  "Other",
];

const gradients = [
  "from-blue-400 to-blue-600",
  "from-green-400 to-green-600",
  "from-purple-400 to-purple-600",
  "from-primary-400 to-primary-600",
  "from-secondary-400 to-secondary-600",
  "from-red-400 to-red-600",
  "from-indigo-400 to-indigo-600",
  "from-pink-400 to-pink-600",
  "from-teal-400 to-teal-600",
];

const Gallery = () => {
  const [filtered, setFiltered] = useState([]);
  const [category, setCategory] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const { data: images = [], loading } = useDataRefresh(
    async () => {
      const response = await galleryAPI.getAll({ limit: 1000 });
      return response?.data?.data?.images || [];
    },
    "gallery",
    { pollInterval: 10000 }, // Refresh every 10 seconds
  );

  useEffect(() => {
    const result =
      category === "All"
        ? images
        : images.filter((i) => i.category === category);
    setFiltered(result);
  }, [category, images]);

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);
  const prevImage = () =>
    setLightbox((l) => (l > 0 ? l - 1 : filtered.length - 1));
  const nextImage = () =>
    setLightbox((l) => (l < filtered.length - 1 ? l + 1 : 0));

  useEffect(() => {
    const handleKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightbox]);

  return (
    <div>
      <div className="hero-gradient py-24 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, #bfdbfe 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <p className="text-secondary-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Our Memories
          </p>
          <h1 className="text-5xl font-serif font-bold text-white mb-4">
            Photo Gallery
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            Explore the vibrant life at Bhujauli English School through our
            collection of photographs
          </p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Filter */}
        <div className="flex items-center gap-3 mb-8 flex-wrap">
          <FiFilter className="text-gray-400 flex-shrink-0" />
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                category === cat
                  ? "bg-primary-500 text-white shadow-md"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="columns-2 sm:columns-3 lg:columns-4 gap-4 space-y-4">
              {filtered.map((image, idx) => (
                <motion.div
                  key={image._id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className="relative break-inside-avoid rounded-2xl overflow-hidden cursor-pointer group"
                  onClick={() => openLightbox(idx)}
                >
                  {image.imageUrl ? (
                    <img
                      src={resolveImageUrl(image.imageUrl)}
                      alt={image.title}
                      className="w-full object-cover"
                    />
                  ) : (
                    <div
                      className={`w-full bg-gradient-to-br ${gradients[idx % gradients.length]} flex items-center justify-center`}
                      style={{ height: `${140 + (idx % 3) * 60}px` }}
                    >
                      <FiImage className="w-10 h-10 text-white/30" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center">
                    <FiZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white text-xs font-medium">
                      {image.title}
                    </p>
                    {image.category && (
                      <p className="text-white/60 text-xs">{image.category}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-20">
                <FiImage className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">
                  No images found for this category
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors z-10"
            >
              <FiX className="w-6 h-6" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors z-10"
            >
              <FiChevronLeft className="w-7 h-7" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors z-10"
            >
              <FiChevronRight className="w-7 h-7" />
            </button>

            <motion.div
              key={lightbox}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full"
            >
              {filtered[lightbox]?.imageUrl ? (
                <img
                  src={resolveImageUrl(filtered[lightbox].imageUrl)}
                  alt={filtered[lightbox].title}
                  className="w-full max-h-[75vh] object-contain rounded-2xl"
                />
              ) : (
                <div
                  className={`w-full h-72 bg-gradient-to-br ${gradients[lightbox % gradients.length]} rounded-2xl flex items-center justify-center`}
                >
                  <FiImage className="w-16 h-16 text-white/30" />
                </div>
              )}
              <div className="text-center mt-4">
                <h3 className="text-white font-semibold">
                  {filtered[lightbox]?.title}
                </h3>
                {filtered[lightbox]?.description && (
                  <p className="text-white/60 text-sm mt-1">
                    {filtered[lightbox].description}
                  </p>
                )}
              </div>
              <div className="text-center mt-3 text-white/40 text-sm">
                {lightbox + 1} / {filtered.length}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
